import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  OnInit
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

/**
 * WCAG 1.4.13 — hoverable tooltips: delay hide on pointer leave so the user can move onto the tooltip.
 * WCAG 2.1.1 / 4.1.2 — show on focus; Escape dismisses; `role="tooltip"` + `aria-describedby` for SR.
 *
 * Template usage: `[appTooltip]="'LABEL' | translate"`.
 *
 * ngx-datatable checkboxes are plain DOM (no template binding) — use {@link ImperativeTooltip} from
 * `./imperative-tooltip` with {@link NG_TOOLTIP_IMPERATIVE_CONFIG} instead of attaching this directive at runtime.
 */
@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements OnInit, AfterViewInit, OnDestroy {
  private static nextTooltipId = 0;

  /** May be a string or a number from template bindings (e.g. room cap). */
  @Input('appTooltip') tooltipTitle: string | number | null | undefined = '';
  /**
   * Optional selector for an element whose box is used for placement only (e.g. datatable `.sort-btn`).
   * Screen reader / aria-describedby stay on the host. If the node is missing, falls back to the host.
   */
  @Input() tooltipPositionAnchor?: string;
  @Input() placement = 'top';
  @Input() delay = 100;
  /** Time to move pointer from host to tooltip before hide (WCAG 1.4.13). */
  @Input() hoverBridgeDelayMs = 500;

  tooltip?: HTMLElement;
  /** Keep tooltip closer to host to reduce pointer “air gap” (WCAG 1.4.13 hoverable). */
  offset = 6;
  readonly tooltipId = `ng-app-tooltip-${TooltipDirective.nextTooltipId++}`;

  private routerSub?: Subscription;
  private leaveHostTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideAnimationTimeout: ReturnType<typeof setTimeout> | null = null;
  private tooltipListeners: Array<() => void> = [];
  /** When `tooltipPositionAnchor` points at a sibling (e.g. datatable `.sort-btn`), bridge pointer hover. */
  private anchorHoverRemoveFns: Array<() => void> = [];
  private anchorHoverRetryTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private router: Router,
    private cdRef: ChangeDetectorRef
  ) {}

  /**
   * Bindings like `[appTooltip]="model.roomCap"` pass numbers; `number` has no `.trim()` and would throw on hover/focus.
   */
  private normalizedTooltipText(): string {
    if (this.tooltipTitle == null) {
      return '';
    }
    if (typeof this.tooltipTitle === 'string') {
      return this.tooltipTitle.trim();
    }
    return String(this.tooltipTitle).trim();
  }

  private hasTooltipContent(): boolean {
    return this.normalizedTooltipText().length > 0;
  }

  ngOnInit(): void {
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.destroyTooltip(true);
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.tooltipPositionAnchor?.trim()) {
      return;
    }
    [0, 100, 250, 500].forEach((ms) => {
      const id = setTimeout(() => this.attachAnchorHoverBridge(), ms);
      this.anchorHoverRetryTimeouts.push(id);
    });
  }

  ngOnDestroy(): void {
    for (const id of this.anchorHoverRetryTimeouts) {
      clearTimeout(id);
    }
    this.anchorHoverRetryTimeouts = [];
    this.detachAnchorHoverBridge();
    this.routerSub?.unsubscribe();
    this.clearLeaveHostTimeout();
    this.clearHideAnimationTimeout();
    this.destroyTooltip(true);
  }

  private openTooltipFromPointer(): void {
    this.clearLeaveHostTimeout();
    this.clearHideAnimationTimeout();
    if (!this.hasTooltipContent()) {
      return;
    }
    if (!this.tooltip) {
      this.show();
      this.cdRef.detectChanges();
    }
  }

  /**
   * Datatable sort headers: `appTooltip` stays on the title {@code <button>} for aria-describedby + focus,
   * but {@code tooltipPositionAnchor=".sort-btn"} places the bubble on the icon. Pointer hover should open
   * the tooltip only on the sort icon, not the full header cell.
   */
  private pointerOpensTooltipOnPositionAnchorOnly(): boolean {
    if (!this.tooltipPositionAnchor?.trim()) {
      return false;
    }
    const anchor = this.getPositionAnchorEl();
    const host = this.el.nativeElement;
    return !!anchor && anchor !== host;
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.pointerOpensTooltipOnPositionAnchorOnly()) {
      return;
    }
    this.openTooltipFromPointer();
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent): void {
    const related = event.relatedTarget as Node | null;
    if (this.shouldSuppressPointerLeaveFor(related)) {
      return;
    }
    this.clearLeaveHostTimeout();
    this.leaveHostTimeout = setTimeout(() => {
      this.leaveHostTimeout = null;
      this.scheduleHide();
    }, this.hoverBridgeDelayMs);
  }

  /** Avoid hiding when moving between host and position anchor (e.g. label button ↔ sort icon). */
  private shouldSuppressPointerLeaveFor(related: Node | null): boolean {
    if (!related) {
      return false;
    }
    const anchor = this.getPositionAnchorEl();
    if (anchor && (anchor === related || anchor.contains(related))) {
      return true;
    }
    if (this.tooltip && (this.tooltip === related || this.tooltip.contains(related))) {
      return true;
    }
    return false;
  }

  private detachAnchorHoverBridge(): void {
    for (const off of this.anchorHoverRemoveFns) {
      off();
    }
    this.anchorHoverRemoveFns = [];
  }

  private attachAnchorHoverBridge(): void {
    this.detachAnchorHoverBridge();
    const anchor = this.getPositionAnchorEl();
    const host = this.el.nativeElement;
    if (!anchor || anchor === host || !this.hasTooltipContent()) {
      return;
    }
    this.anchorHoverRemoveFns.push(
      this.renderer.listen(anchor, 'mouseenter', () => this.openTooltipFromPointer())
    );
    this.anchorHoverRemoveFns.push(
      this.renderer.listen(anchor, 'mouseleave', (e: Event) => {
        const ev = e as MouseEvent;
        const related = ev.relatedTarget as Node | null;
        if (this.tooltip && related && (this.tooltip === related || this.tooltip.contains(related))) {
          return;
        }
        this.clearLeaveHostTimeout();
        this.leaveHostTimeout = setTimeout(() => {
          this.leaveHostTimeout = null;
          this.scheduleHide();
        }, this.hoverBridgeDelayMs);
      })
    );
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent): void {
    if (!this.hasTooltipContent()) {
      return;
    }
    if (!this.el.nativeElement.contains(event.target as Node)) {
      return;
    }
    if (!this.tooltip) {
      this.show();
      this.cdRef.detectChanges();
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && this.tooltip && this.tooltip.contains(next)) {
      return;
    }
    queueMicrotask(() => {
      const ae = document.activeElement;
      if (this.tooltip?.contains(ae)) {
        return;
      }
      if (this.el.nativeElement.contains(ae)) {
        return;
      }
      this.scheduleHide();
    });
  }

  @HostListener('click')
  onClick(): void {
    this.destroyTooltip(true);
    this.cdRef.detectChanges();
  }

  /** WCAG 1.4.13 — dismiss additional content with Escape (hover- or focus-opened). */
  /** Use `keydown.escape` so we do not register a document handler for every key (Tab on busy pages e.g. My Schedule would run this × N tooltips). */
  @HostListener('document:keydown.escape', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.tooltip) {
      return;
    }
    if (!document.body.contains(this.tooltip)) {
      this.tooltip = undefined;
      return;
    }
    const host = this.el.nativeElement;
    const ae = document.activeElement;
    const focusOnHostOrTooltip =
      !!ae && (host === ae || host.contains(ae) || this.tooltip.contains(ae));
    if (focusOnHostOrTooltip) {
      event.preventDefault();
    }
    this.destroyTooltip(true);
    this.restoreFocusToHostIfNeeded();
    this.cdRef.detectChanges();
  }

  /** After Escape, return keyboard focus to the control that opened the tooltip (WCAG 1.4.13). */
  private restoreFocusToHostIfNeeded(): void {
    const host = this.el.nativeElement;
    if (typeof host.matches !== 'function') {
      return;
    }
    if (
      host.matches(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ) {
      host.focus();
    }
  }

  show(): void {
    if (!this.hasTooltipContent()) {
      return;
    }
    this.removeExistingTooltips();
    this.create();
    requestAnimationFrame(() => {
      if (!this.tooltip) {
        return;
      }
      this.setPosition();
      this.renderer.addClass(this.tooltip, 'ng-tooltip-show');
      this.attachDescribedBy();
    });
  }

  private scheduleHide(): void {
    this.destroyTooltip(false);
  }

  private destroyTooltip(immediate: boolean): void {
    this.clearLeaveHostTimeout();
    this.clearHideAnimationTimeout();
    if (immediate) {
      this.teardownTooltipDom();
      return;
    }
    this.hideAnimationTimeout = setTimeout(() => {
      this.hideAnimationTimeout = null;
      this.teardownTooltipDom();
    }, this.delay);
  }

  private teardownTooltipDom(): void {
    this.detachDescribedBy();
    for (const off of this.tooltipListeners) {
      off();
    }
    this.tooltipListeners = [];
    if (this.tooltip) {
      this.renderer.removeClass(this.tooltip, 'ng-tooltip-show');
      const parent = this.tooltip.parentNode;
      if (parent) {
        this.renderer.removeChild(parent, this.tooltip);
      }
      this.tooltip = undefined;
    }
  }

  private clearLeaveHostTimeout(): void {
    if (this.leaveHostTimeout != null) {
      clearTimeout(this.leaveHostTimeout);
      this.leaveHostTimeout = null;
    }
  }

  private clearHideAnimationTimeout(): void {
    if (this.hideAnimationTimeout != null) {
      clearTimeout(this.hideAnimationTimeout);
      this.hideAnimationTimeout = null;
    }
  }

  private removeExistingTooltips(): void {
    document.querySelectorAll('.ng-tooltip').forEach((node) => {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
  }

  hide(): void {
    this.destroyTooltip(false);
  }

  private create(): void {
    this.tooltip = this.renderer.createElement('span');
    this.renderer.setAttribute(this.tooltip, 'id', this.tooltipId);
    this.renderer.setAttribute(this.tooltip, 'role', 'tooltip');
    this.renderer.setStyle(this.tooltip, 'pointer-events', 'auto');

    this.renderer.appendChild(this.tooltip, this.renderer.createText(this.normalizedTooltipText()));

    this.renderer.appendChild(document.body, this.tooltip);
    this.renderer.addClass(this.tooltip, 'ng-tooltip');
    this.renderer.addClass(this.tooltip, `ng-tooltip-${this.placement}`);

    this.renderer.setStyle(this.tooltip, '-webkit-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, '-moz-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, '-o-transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, 'transition', `opacity ${this.delay}ms`);
    this.renderer.setStyle(this.tooltip, 'position', 'fixed');
    this.renderer.setStyle(this.tooltip, 'top', '-9999px');
    this.renderer.setStyle(this.tooltip, 'left', '-9999px');

    this.tooltipListeners.push(
      this.renderer.listen(this.tooltip, 'mouseenter', () => {
        this.clearLeaveHostTimeout();
      })
    );
    this.tooltipListeners.push(
      this.renderer.listen(this.tooltip, 'mouseleave', () => {
        this.scheduleHide();
      })
    );
  }

  private attachDescribedBy(): void {
    if (!this.hasTooltipContent()) {
      return;
    }
    const host = this.el.nativeElement;
    const existing = host.getAttribute('aria-describedby')?.trim() || '';
    const ids = new Set(existing.split(/\s+/).filter(Boolean));
    ids.add(this.tooltipId);
    host.setAttribute('aria-describedby', [...ids].join(' '));
  }

  private detachDescribedBy(): void {
    const host = this.el.nativeElement;
    const existing = host.getAttribute('aria-describedby')?.trim();
    if (!existing) {
      return;
    }
    const ids = existing.split(/\s+/).filter((id) => id !== this.tooltipId);
    if (ids.length) {
      host.setAttribute('aria-describedby', ids.join(' '));
    } else {
      host.removeAttribute('aria-describedby');
    }
  }

  /**
   * Invalid selectors throw from `querySelector`; external `tooltipPositionAnchor` must not break the
   * directive — return null so positioning falls back to the host rect.
   */
  private querySelectorSafe(root: ParentNode | null | undefined, selector: string): HTMLElement | null {
    if (!root) {
      return null;
    }
    try {
      return root.querySelector(selector) as HTMLElement | null;
    } catch {
      return null;
    }
  }

  private getPositionAnchorEl(): HTMLElement | null {
    const host = this.el.nativeElement;
    const sel = this.tooltipPositionAnchor?.trim();
    if (!sel) {
      return null;
    }
    let wrap: HTMLElement | null = null;
    try {
      wrap = host.closest('.datatable-header-cell-template-wrap') as HTMLElement | null;
    } catch {
      wrap = null;
    }
    return this.querySelectorSafe(host.parentElement, sel) ?? this.querySelectorSafe(wrap, sel) ?? null;
  }

  private getPositionRectForTooltip(): DOMRect | undefined {
    const host = this.el.nativeElement;
    const anchor = this.getPositionAnchorEl();
    if (anchor) {
      return anchor.getBoundingClientRect();
    }
    return host?.getBoundingClientRect();
  }

  setPosition(): void {
    const hostPos = this.getPositionRectForTooltip();
    if (!hostPos || !this.tooltip) {
      return;
    }

    const tooltipWidth = this.tooltip.offsetWidth || this.tooltip.getBoundingClientRect().width;
    const tooltipHeight = this.tooltip.offsetHeight || this.tooltip.getBoundingClientRect().height;

    let top: number;
    let left: number;

    if (this.placement === 'top') {
      top = hostPos.top - tooltipHeight - this.offset;
      left = hostPos.left + hostPos.width / 2 - tooltipWidth / 2;
    } else if (this.placement === 'bottom') {
      top = hostPos.bottom + this.offset;
      left = hostPos.left + hostPos.width / 2 - tooltipWidth / 2;
    } else if (this.placement === 'left') {
      top = hostPos.top + hostPos.height / 2 - tooltipHeight / 2;
      left = hostPos.left - tooltipWidth - this.offset;
    } else if (this.placement === 'right') {
      top = hostPos.top + hostPos.height / 2 - tooltipHeight / 2;
      left = hostPos.right + this.offset;
    } else {
      top = hostPos.top - tooltipHeight - this.offset;
      left = hostPos.left + hostPos.width / 2 - tooltipWidth / 2;
    }

    this.renderer.setStyle(this.tooltip, 'position', 'fixed');
    this.renderer.setStyle(this.tooltip, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }

  public getTooltipText(): string {
    const classList = this.el.nativeElement.classList;

    if (classList.contains('status-active')) {
      return 'Active';
    } else if (classList.contains('status-inactive')) {
      return 'Inactive';
    }

    return '';
  }
}
